import { ScoredProject } from '@/lib/types'
import { daysSinceWorked } from '@/lib/scoring'

interface ProjectListProps {
  projects: ScoredProject[]
  currentMoodLabel: string
  previewProjectId?: string  // For highlighting preview projects
  onEditProject?: (project: ScoredProject) => void
  onDeleteProject?: (projectId: string) => void
  onMarkWorked?: (projectId: string) => void
}

/**
 * How long a project has gone untouched, for display. Uses the same clock as
 * the scoring (daysSinceWorked) rather than re-deriving the createdAt
 * fallback, so the badge can never disagree with the rank it explains.
 */
function neglectLabel(project: ScoredProject): { text: string; stale: boolean } | null {
  const days = Math.floor(daysSinceWorked(project))
  if (days < 7) return null            // nothing interesting to say yet
  return { text: `${days}d untouched`, stale: days >= 30 }
}

export default function ProjectList({ projects, currentMoodLabel, previewProjectId, onEditProject, onDeleteProject, onMarkWorked }: ProjectListProps) {
  if (projects.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 dark:text-gray-500 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No projects yet</h3>
        <p className="text-gray-500 dark:text-gray-400">
          Add your first project to see mood-responsive prioritization in action!
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Your Projects
        </h2>
        <div className="flex items-center space-x-4">
          {previewProjectId && (
            <span className="text-sm text-mood-600 dark:text-mood-400 font-medium">
              ✨ Live Preview
            </span>
          )}
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Ranked for {currentMoodLabel} mood
          </span>
        </div>
      </div>
      
      <div className="grid gap-4">
        {projects.map((project, index) => {
          const isPreview = project.id === previewProjectId
          
          return (
            <div
              key={project.id}
              className={`rounded-lg border shadow-sm hover:shadow-md transition-all p-6 ${
                isPreview
                  ? 'border-mood-400 bg-mood-50/50 dark:bg-mood-900/20 ring-2 ring-mood-300/50 dark:ring-mood-600/50' 
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      isPreview 
                        ? 'bg-mood-200 dark:bg-mood-800 text-mood-800 dark:text-mood-200'
                        : 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                    }`}>
                      #{index + 1}
                    </span>
                    <h3 className={`text-lg font-semibold ${
                      isPreview
                        ? 'text-mood-900 dark:text-mood-100'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {project.title}
                      {isPreview && (
                        <span className="ml-2 text-xs font-normal text-mood-600 dark:text-mood-400">
                          (Preview)
                        </span>
                      )}
                    </h3>
                  </div>
                  
                  <p className={`mb-4 ${
                    isPreview
                      ? 'text-mood-700 dark:text-mood-300'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}>
                    {project.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <span className="font-medium">Importance:</span> {project.importance}/3
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="font-medium">Urgency:</span> {project.urgency}/3
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="font-medium">Enjoyment:</span> {project.enjoyment}/5
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="font-medium">Scope:</span> {project.scope}/3
                    </span>
                    {!isPreview && (() => {
                      const neglect = neglectLabel(project)
                      if (!neglect) return null
                      return (
                        <span
                          className={`flex items-center gap-1 font-medium ${
                            neglect.stale
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                          title="Time since you last marked work on this. Neglect raises urgency."
                        >
                          ⏳ {neglect.text}
                        </span>
                      )
                    })()}
                  </div>

                  {!isPreview && onMarkWorked && (
                    <button
                      onClick={() => onMarkWorked(project.id)}
                      className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-primary-200 dark:border-primary-800 px-3 py-1.5 text-sm font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                      title="Resets this project's neglect clock. Only press it if you actually did the work."
                    >
                      ✓ I worked on this
                    </button>
                  )}
                </div>
                
                <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                  <div className={`px-3 py-1 rounded-full text-sm font-bold text-white ${
                    isPreview
                      ? 'bg-gradient-to-r from-mood-500 to-mood-600'
                      : 'bg-gradient-to-r from-primary-500 to-mood-600'
                  }`}>
                    {Math.round(project.score * 100)}
                  </div>
                  
                  {/* Action buttons - only show for non-preview projects */}
                  {!isPreview && (onEditProject || onDeleteProject) && (
                    <div className="flex items-center gap-1">
                      {onEditProject && (
                        <button
                          onClick={() => onEditProject(project)}
                          className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                          title="Edit project"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      )}
                      {onDeleteProject && (
                        <button
                          onClick={() => onDeleteProject(project.id)}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Delete project"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Preview indicator */}
              {isPreview && (
                <div className="mt-4 flex items-center text-xs text-mood-600 dark:text-mood-400">
                  <div className="animate-pulse w-2 h-2 bg-mood-500 rounded-full mr-2"></div>
                  This project will appear here when saved
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}